import { useTheme } from '@mui/styles';
import { AuthInterface } from '@tassat/common-definitions';
import React from 'react';
import { Redirect, Route } from 'react-router-dom';

import PageLoading from 'Containers/common/pageLoading';
import authServiceMap from 'Lib/auth/authServiceMap';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

import { BaseProtectedRouteProps, ProtectedRouteProps } from './types';

enum AuthenticationState {
    PENDING_AUTHENTICATION, //state to manage refresh token scenario where second attempt to renew token delays rendering the UI.
    AUTHENTICATED,
    UNAUTHENTICATED
}

const ProtectedRoute = (props: BaseProtectedRouteProps) => {
    const muiTheme = useTheme<ApplicationMuiTheme>();
    const loginMethod: string = muiTheme?.app?.clientTheme.features.Authentication.LoginMethod;
    const [ authenticationState, setAuthenticationState ] = React.useState<AuthenticationState>(AuthenticationState.PENDING_AUTHENTICATION);
    const authServiceRef = React.useRef<AuthInterface>(null);
    const {
        component: Component,
        forAdmin,
        ...rest
    } = props;

    React.useEffect(() => {
        const getAuthenticationState = async () => {
            if (!authServiceRef.current) {
                authServiceRef.current = await authServiceMap[loginMethod]();
            }

            if (authServiceRef.current) {
                const isAuthenticated = await authServiceRef.current.isAuthenticated();
                setAuthenticationState(isAuthenticated ? AuthenticationState.AUTHENTICATED : AuthenticationState.UNAUTHENTICATED);
            } else {
                console.error(`AuthService not found for login method: ${loginMethod}`);
            }
        };

        getAuthenticationState();
    }, [props.location.pathname]);

    return (
        <Route
            {...rest}
            render={(props) => {
                //NOTE: Thanks to React reconiliation, pending authentication will only happen during refresh since all routes are handled
                //by a single component ProtectedAdminRoute or ProtectedClientRoute so React retains the component
                //and its state between route changes.
                //Refresh: Pending authentication state
                //Route change: Either authenticated or unauthenticated state computed based on existence of token in browser storage.
                if (AuthenticationState.PENDING_AUTHENTICATION === authenticationState) {
                    return <PageLoading />;
                } else if (AuthenticationState.AUTHENTICATED === authenticationState) {
                    return <Component {...props} />;
                }

                return <Redirect to={forAdmin ? '/admin_auth_login' : '/auth_login'} />;
            }}
        />
    );
};

export const ProtectedAdminRoute = (props: ProtectedRouteProps) => {
    const {
        component: Component,
        ...rest
    } = props;

    return (
        <ProtectedRoute
            {...rest}
            component={(componentProps) => (
                <Component {...componentProps} />
            )}
            forAdmin={true}
        />
    );
};

export const ProtectedClientRoute = (props: ProtectedRouteProps) => {
    const {
        component: Component,
        ...rest
    } = props;

    return (
        <ProtectedRoute
            {...rest}
            component={(componentProps) => (
                <Component {...componentProps} />
            )}
            forAdmin={false}
        />
    );
};